import Vue from "vue";
import VueRouter from "vue-router";
import Home from "@/views/Home/index.vue";
import Login from "@/views/Login.vue";
import store from "@/store";

import auth from "@/api/auth";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "Dashboard",
    meta: {
      icon: "home",
      navShow: true,
    },
    component: Home,
  },
  {
    path: "/customers",
    name: "Customers",
    meta: {
      icon: "user-group",
      navShow: true,
      hideFor: ["bulk-inventory"],
    },
    component: () => import("@/views/Customers/index.vue"),
    children: [
      {
        path: "details/:id?",
        name: "Details",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "customers" */ "@/views/Customers/Details.vue"
          ),
        props: {},
      },
      {
        path: "orders/:orderNumber?",
        name: "Orders",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "customers" */ "@/views/Customers/Orders.vue"
          ),
        props: true,
      },

      {
        path: "search",
        name: "Search",
        meta: {
          navShow: false,
        },
        component: () =>
          import(
            /* webpackChunkName: "customers" */ "@/views/Customers/QuickSearchResults.vue"
          ),
      },
    ],
  },
  {
    path: "/reports",
    name: "Reports",
    meta: {
      icon: "chart-bar",
      navShow: true,
      hideFor: ["bulk-inventory"],
    },
    component: () =>
      import(/* webpackChunkName: "reports" */ "@/views/Reports/index.vue"),
    children: [
      {
        path: "bad-address",
        name: "Bad Address",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "reports" */ "@/views/Reports/BadAddressesReport.vue"
          ),
      },
      {
        path: "unprocessed-orders",
        name: "Unprocessed Orders",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "reports" */ "@/views/Reports/UnprocessedOrders.vue"
          ),
      },
      {
        path: "freight-review",
        name: "Freight Review",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "reports" */ "@/views/Reports/FreightReview.vue"
          ),
      },
      {
        path: "fraud-orders",
        name: "Fraud Orders",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "reports" */ "@/views/Reports/FraudOrders.vue"
          ),
      },
      {
        path: "state-restrictions",
        name: "State Restrictions",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "reports" */ "@/views/Reports/NewStateRestrictions.vue"
          ),
      },
      {
        path: "on-hold-summary",
        name: "On Hold Summary",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "reports" */ "@/views/Reports/OnHoldSummary.vue"
          ),
      },
      {
        path: "balance-due-report",
        name: "Balance Due Report",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "reports" */ "@/views/Reports/BalanceDueReport.vue"
          ),
      },
    ],
  },
  {
    path: "/shopify",
    name: "Shopify",
    meta: {
      icon: "shopping-cart",
      navShow: true,
      hideFor: ["bulk-inventory"],
    },
    component: () =>
      import(/* webpackChunkName: "shopify" */ "@/views/Shopify/index.vue"),
    children: [
      {
        path: "discounts",
        name: "Discounts",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "shopify" */ "@/views/Shopify/Discounts.vue"
          ),
      },
      {
        path: "gift-cards",
        name: "GiftCards",
        meta: {
          navShow: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "shopify" */ "@/views/Shopify/GiftCards.vue"
          ),
      },
    ],
  },
  {
    path: "/utilities",
    name: "Utilities",
    meta: {
      icon: "beaker",
      navShow: true,
      showFor: [
        "supervisor",
        "third-party",
        "bulk-inventory",
        "purchasing",
        "supervisor_plus",
      ],
    },
    component: () =>
      import(/* webpackChunkName: "utilities" */ "@/views/Utilities/index.vue"),
    children: [
      {
        path: "bulk-email",
        name: "Bulk Email",
        meta: {
          navShow: true,
          hideFor: ["bulk-inventory", "purchasing", "supervisor_plus"],
        },
        component: () =>
          import(
            /* webpackChunkName: "utilities" */ "@/views/Utilities/BulkEmail.vue"
          ),
      },
      {
        path: "manual-import",
        name: "Manual Import",
        meta: {
          navShow: true,
          showFor: ["admin", "supervisor_plus"],
        },
        component: () =>
          import(
            /* webpackChunkName: "utilities" */ "@/views/Utilities/ManualImport.vue"
          ),
      },
      {
        path: "backorders-status",
        name: "Backorders Status",
        meta: {
          navShow: true,
          showFor: ["third-party"],
        },
        component: () =>
          import(
            /* webpackChunkName: "utilities" */ "@/views/Utilities/BackordersStatus.vue"
          ),
      },
    ],
  },
  {
    path: "/login",
    name: "Login",
    meta: {
      guest: true,
      nav: false,
    },
    component: Login,
  },
  {
    path: "/auth",
    name: "Auth",
    meta: {
      guest: true,
      nav: false,
    },
    beforeEnter() {
      auth.auth.getSession();
    },
  },
  {
    path: "/login/oauth2/code/cognito",
    name: "Cognito",
    meta: {
      guest: true,
      nav: false,
    },
    beforeEnter(to, from, next) {
      var currUrl = window.location.href;
      auth.auth.parseCognitoWebResponse(currUrl);
      next({ name: "Dashboard" });
    },
  },
  {
    path: "/logout",
    name: "Logout",
    meta: {
      guest: true,
      nav: false,
    },
    component: Login,
    beforeEnter(to, from, next) {
      auth.logout();
      store.dispatch("logout");
      next();
    },
  },
  {
    path: "/notifications",
    name: "Notifications",
    meta: {
      nav: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "notifications" */ "@/views/Notifications.vue"
      ),
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

function canAccessRoute(route) {
  let parent = getRouteParent(route);

  // A user should not be alowed to access any child route if it cannot access the parent
  if (parent !== undefined && !canAccessRoute(parent)) {
    return false;
  }

  if (route?.meta?.showFor?.length) {
    return auth.isUserAuthorized(route.meta.showFor);
  }

  if (auth.denyFor(route?.meta?.hideFor || [])) {
    return false;
  }

  return true;
}

/**
 * This function currently identifies only the parent of the first matched route.
 * Some scenarios can be encountered (usually with poorly defined routes) where
 * multiple matched routes might have a parent. Given the rare incidence of these
 * scenarios, the method has been kept in its simplest form.
 */
function getRouteParent(route) {
  return route?.matched?.find((matched) => matched?.parent !== undefined)
    ?.parent;
}

// If the user is not authenticated redirect them to login
router.beforeEach((to, from, next) => {
  // any guest route we can just go to
  if (to.meta.guest) {
    next();
    return;
  }

  if (!auth.auth.isUserSignedIn()) {
    next({ name: "Logout" });
    return;
  }

  store.dispatch("initializeUser").then(() => {
    if (!canAccessRoute(to)) {
      next({ path: "/" });
      return;
    }

    next();
  });
});

export default router;
